import { useQuery } from '@tanstack/react-query';
import { timeEntriesApi } from '../../api/timeEntries';
import type { TimeEntry } from '../../types';

interface TimeEntryListProps {
  ticketId: string;
}

export default function TimeEntryList({ ticketId }: TimeEntryListProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['time-entries', ticketId],
    queryFn: () => timeEntriesApi.list({ ticketId }),
    enabled: !!ticketId,
  });

  const entries = (data?.entries ?? []) as TimeEntry[];
  const totalMinutes = entries.reduce((acc, e) => acc + e.durationMinutes, 0);
  const billableMinutes = entries.filter((e) => e.billable).reduce((acc, e) => acc + e.durationMinutes, 0);

  if (isLoading) return <div className="text-sm text-muted">Loading time entries...</div>;

  if (entries.length === 0) {
    return <p className="text-sm text-muted">No time entries yet.</p>;
  }

  const techName = (e: TimeEntry) => (e as any).technician?.name ?? (e as any).technicianId ?? 'Unknown';

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm mb-2">
        <span className="text-muted">Total: {(totalMinutes / 60).toFixed(1)} hrs</span>
        <span className="text-muted">Billable: {(billableMinutes / 60).toFixed(1)} hrs</span>
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {entries.map((e) => (
          <div
            key={(e as any).id ?? (e as any)._id}
            className="flex justify-between text-sm py-2 border-b border-line-light last:border-0"
          >
            <div>
              <span className="font-medium text-fg">{techName(e)}</span>
              <span className="text-muted ml-2">{(e.durationMinutes / 60).toFixed(1)}h</span>
              {e.billable && (
                <span
                  className="ml-1.5 text-xs font-bold"
                  style={{ color: 'var(--scpa-status-ok)' }}
                  title="Billable"
                >
                  $
                </span>
              )}
            </div>
            <span className="text-muted text-xs">
              {e.createdAt ? new Date(e.createdAt).toLocaleDateString() : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
